/**
 * Candles for the stock chart, made from the market data the server hands out. The server knows a few
 * candle sizes (1m 5m 15m 1h 1D 1W 1M); the others a trader asks for are put together from them here
 * (two 5-minute candles make a 10-minute one, two or three days make a 2D / 3D candle).
 */
import type { OhlcBar } from '@shared/types'

/** Candles, oldest first. */
export interface Bars {
  o: number[]
  h: number[]
  l: number[]
  c: number[]
  v: number[]
  labels: string[]
  rsi: number[]
}

/** How much time one candle holds. */
export type Interval = '1m' | '5m' | '10m' | '1h' | '2h' | '1D' | '2D' | '3D' | '1W' | '1M'
export const INTERVALS: Interval[] = ['1m', '5m', '10m', '1h', '2h', '1D', '2D', '3D', '1W', '1M']
/** What to ask the server for, and how many of those make one candle. */
export const SOURCE: Record<Interval, { timeframe: string; group: number }> = {
  '1m': { timeframe: '1m', group: 1 },
  '5m': { timeframe: '5m', group: 1 },
  '10m': { timeframe: '5m', group: 2 },
  '1h': { timeframe: '1h', group: 1 },
  '2h': { timeframe: '1h', group: 2 },
  '1D': { timeframe: '1D', group: 1 },
  '2D': { timeframe: '1D', group: 2 },
  '3D': { timeframe: '1D', group: 3 },
  '1W': { timeframe: '1W', group: 1 },
  '1M': { timeframe: '1M', group: 1 },
}

/** Wilder's RSI(14) over closes. */
function wilder(c: number[]) {
  const out = Array<number>(c.length).fill(50)
  let g = 0
  let l = 0
  for (let i = 1; i < c.length; i++) {
    const ch = c[i] - c[i - 1]
    g = (g * 13 + Math.max(ch, 0)) / 14
    l = (l * 13 + Math.max(-ch, 0)) / 14
    out[i] = l ? 100 - 100 / (1 + g / l) : 100
  }
  return out
}

const label = (t: number | string, intraday: boolean) => {
  const d = typeof t === 'number' ? new Date(t * 1000) : new Date(`${t}T12:00:00`)
  return intraday ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

/** The server's bars as the chart draws them, `group` of them to a candle (counted from the newest, so the last candle is whole). */
export function toBars(raw: OhlcBar[], group = 1): Bars {
  const intraday = typeof raw[0]?.time === 'number'
  const out: Bars = { o: [], h: [], l: [], c: [], v: [], labels: [], rsi: [] }
  const lead = raw.length % group
  for (let i = lead; i < raw.length; i += group) {
    const part = raw.slice(i, i + group)
    out.o.push(part[0].open)
    out.h.push(Math.max(...part.map((b) => b.high)))
    out.l.push(Math.min(...part.map((b) => b.low)))
    out.c.push(part[part.length - 1].close)
    out.v.push(part.reduce((a, b) => a + (b.volume ?? 0), 0))
    out.labels.push(label(part[part.length - 1].time, intraday))
  }
  out.rsi = wilder(out.c)
  return out
}

/** A simple moving average over closes (over what there is, for the first few). */
export function movingAverage(c: number[], n: number): number[] {
  const out: number[] = []
  let sum = 0
  for (let i = 0; i < c.length; i++) {
    sum += c[i]
    if (i >= n) sum -= c[i - n]
    out.push(sum / Math.min(i + 1, n))
  }
  return out
}
